// Shared research-tree data — single source of truth for both the in-game
// research panel (apps/client/src/main.ts) and the wiki tier pages
// (apps/client/src/docs/index.ts). When updating tech card copy / icons /
// layout, edit here once.

export type ResearchChoice = { id: string; name: string; icon: string; desc: string };
export type ResearchNode = {
  id: string;
  name: string;
  /** Gold cost. Tier nodes are gold-paid; branch nodes set this to 0 and use tpCost. */
  cost: number;
  /** Tech-point cost. Branch nodes are 1; tier nodes leave this undefined and pay gold via cost. */
  tpCost?: number;
  /** Layout row in the research panel — drives top-to-bottom placement. */
  row: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  prereqs?: string[];
  /** If present, this slot is a pick-one: clicking opens a popover to commit to one choice. */
  choices?: ResearchChoice[];
  /** Multi-level / repeatable (T3 B1 Lane Expansion). Never reads as "Researched"; cost stays purchasable each time. */
  repeatable?: true;
};

// Layout: every tier spine sits alone on its own centered row, with branches
// on the row immediately below. Tier-up rewards +2 tech points server-side.
// Branch costs are tech-points (1 pt each); tier nodes stay gold-paid.
export const RESEARCH_TREE: ResearchNode[] = [
  { id: "tier_1", name: "Tier 1", cost: 0, row: 1 },

  { id: "t1_b1_cannon", name: "Cannon Tower", cost: 0, tpCost: 1, row: 2, prereqs: ["tier_1"] },
  {
    id: "t1_b2_u4", name: "Call to Arms", cost: 0, tpCost: 1, row: 2, prereqs: ["tier_1"],
    choices: [
      { id: "t1_b2_elder",   name: "Elder",   icon: "elder",
        desc: "The strongest in the village." },
      { id: "t1_b2_footmen", name: "Footmen", icon: "footmen",
        desc: "A squad of three men." },
    ],
  },

  // Tier spines have no prereq chain — any UNLOCK button is always clickable
  // (once you can afford it). The cost curve does the gating naturally.
  { id: "t2_main", name: "Tier 2", cost: 1200, row: 3 },

  { id: "t2_b3_harpoon", name: "Harpoon Tower", cost: 0, tpCost: 1, row: 4, prereqs: ["t2_main"] },
  {
    id: "t2_b2_u4", name: "The Pit", cost: 0, tpCost: 1, row: 4, prereqs: ["t2_main"],
    choices: [
      { id: "t2_b2_juggernaut", name: "Juggernaut", icon: "juggernaut",
        desc: "Unrelenting and massive armored unit." },
      { id: "t2_b2_raider",     name: "Raider",     icon: "raider",
        desc: "Stops to destroy enemy towers." },
    ],
  },
  { id: "t2_b1_airmount", name: "Sky Lancer", cost: 0, tpCost: 1, row: 4, prereqs: ["t2_main"] },

  // Lane Expansion: multi-level / bottomless. Moved to a T1 slot 2026-05-13;
  // the id keeps its t3_ prefix for back-compat with already-shipped server.
  { id: "t3_b1_lane_expansion", name: "Lane Expansion", cost: 0, tpCost: 1, row: 2, prereqs: ["tier_1"], repeatable: true },

  { id: "t3_main", name: "Tier 3", cost: 20000,   row: 5 },
  { id: "t4_main", name: "Tier 4", cost: 300000,  row: 7 },
  { id: "t5_main", name: "Tier 5", cost: 2000000, row: 9 },
];

// Silhouette key shown on each research node — for unit-pack nodes we show a
// single strongest-unit stand-in because a 2×2 composite is unreadable at ~44px.
export const RESEARCH_ICON: Record<string, string> = {
  t1_b1_cannon:        "cannon",
  t2_main:             "lancer",
  t2_b3_harpoon:       "harpoon",
  t2_b1_airmount:      "skyLancer",
  t3_main:             "paladin",
  t4_main:             "warGolem",
  t5_main:             "titan",
  t3_b1_lane_expansion: "laneExpansion",
};

export type TechInfo = { desc: string; grants: string[] };
export const TECH_INFO: Record<string, TechInfo> = {
  tier_1: {
    desc: "The barebones of any kingdom — a watchtower on the wall, peasants and chickens to throw at trouble.",
    grants: [
      "Build: Arrow Tower (10g)",
      "Upgrade: Arrow → Ballista (30g/tower, no research)",
      "Send: Chicken, Peasant, Militia",
    ],
  },
  t1_b1_cannon: {
    desc: "Unlocks the Ballista → Cannon upgrade. AoE damage, ground only.",
    grants: [
      "Allows to upgrade from Ballista tower to Cannon tower",
      "Cannon tower deals AoE damage to ground units only",
    ],
  },
  t1_b2_u4: {
    desc: "Choose between two reinforcements:",
    grants: [
      "Elder: single strong unit.",
      "Footmen: organised squad of units",
    ],
  },
  t1_b2_elder: {
    desc: "Armored elder — divine resolve made flesh.",
    grants: ["Send: Elder"],
  },
  t1_b2_footmen: {
    desc: "Three town-watch footmen, halberds in hand.",
    grants: ["Send: Footmen"],
  },
  t2_main: {
    desc: "Standing army — squires of the lesser houses, hardened soldiers, and noble lancers answer the call.",
    grants: [
      "Send: Squire, Soldier, Lancer",
      "Upgrade: Ballista → Arbalest (200g/tower)",
      "Upgrade: Cannon → Mortar (200g/tower)",
    ],
  },
  t2_b1_airmount: {
    desc: "Substitutes Lancer with a Sky Lancer — flies over towers.",
    grants: [
      "Replaces Lancer with Sky Lancer",
      "Sky Lancers are an Air unit that fly over towers",
    ],
  },
  t2_b2_u4: {
    desc: "Choose between two reinforcements:",
    grants: [
      "Juggernaut: massive armored bulwark.",
      "Raider: siege monster that smashes towers.",
    ],
  },
  t2_b2_juggernaut: {
    desc: "A mountain of muscle — breaks through by sheer mass.",
    grants: ["Send: Juggernaut"],
  },
  t2_b2_raider: {
    desc: "Siege monster — towers crumble in its wake.",
    grants: ["Send: Raider"],
  },
  t2_b3_harpoon: {
    desc: "Unlocks the Ballista → Harpoon upgrade. Hits air units only.",
    grants: [
      "Allows to upgrade from Ballista tower to Harpoon tower",
      "Harpoon tower deals damage to air units only",
    ],
  },
  t3_main: {
    desc: "Champions — duelists, berserkers, paladins, and the riders of the great griffins. Each unit fights with a distinct identity.",
    grants: [
      "Send: Duelist, Berserker, Paladin, Griffin Rider",
      "Upgrade: T2 towers → Polybolos / Howitzer / Skyhook",
    ],
  },
  t3_b1_lane_expansion: {
    desc: "Adds 3 more rows to the lane. Researchable multiple times.",
    grants: [
      "This power can stack multiple times",
      "Unlock 3 more rows in the lane.",
    ],
  },
  t4_main: {
    desc: "Beasts and war constructs — minotaur war-bands, behemoth siege beasts, wyverns from the high mountains, golems carved by the old smiths.",
    grants: [
      "Send: Minotaurs, Behemoth, Wyvern, War Golem",
      "Upgrade: T3 towers → Skorpion / Culverin / Stormpike",
    ],
  },
  t5_main: {
    desc: "Titans — the cosmic apex. Shinigami, dragons, colossi. HP totals in the millions per body; one T5 send is its own match-deciding sentence.",
    grants: [
      "Send: Shinigami, Dragon, Titan, Colossus",
      "Upgrade: T4 towers → Manticore / Bombard / Skybreaker",
    ],
  },
};
